import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { getUsersService, getBikesService } from "modules/came/services/bicicletasService";

import { hasRecordId } from "../utils/recordIds";

const useFormBicicletaModal = (initialData = {}, callbacks = {}, usersFromParent = []) => {
  const initialRef = useRef(initialData);

  const [formData, setFormData] = useState({
    cedula: initialRef.current?.cedula || "",
    idType: initialRef.current?.idType || 1,
    nombre: initialRef.current?.nombre || "",
    apellido: initialRef.current?.apellido || "",
    celular: initialRef.current?.celular || "",
    color: initialRef.current?.color || "",
    marca: initialRef.current?.marca || "",
    serie: initialRef.current?.serie || "",
    ganchoId: initialRef.current?.ganchoId || "",
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [savedUser, setSavedUser] = useState(null);
  const [savedBike, setSavedBike] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [savingBike, setSavingBike] = useState(false);
  const [userError, setUserError] = useState(null);
  const [bikeError, setBikeError] = useState(null);

  const [internalUsers, setInternalUsers] = useState([]);
  const [internalBikes, setInternalBikes] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBikes, setLoadingBikes] = useState(false);

  const [searchingUser, setSearchingUser] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [userLookupDone, setUserLookupDone] = useState(false);
  const [userBikes, setUserBikes] = useState([]);

  const isMounted = useRef(true);
  useEffect(() => {
    if (savedUser && !hasRecordId(savedUser.id)) {
      setSavedUser(null);
      setSavedBike(null);
      setUserError("No se pudo recuperar el ID del usuario. Búscalo nuevamente por documento.");
    }
  }, [savedUser]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(usersFromParent) && usersFromParent.length > 0) {
      setInternalUsers(usersFromParent);
    }
  }, [usersFromParent]);

  useEffect(() => {
    if (Array.isArray(usersFromParent) && usersFromParent.length > 0) {
      return;
    }

    let mounted = true;

    const load = async () => {
      setLoadingUsers(true);
      try {
        const result = await getUsersService();
        if (mounted && result?.success) {
          setInternalUsers(Array.isArray(result.data) ? result.data : []);
        }
      } catch (err) {
        console.error("Error al cargar usuarios en modal:", err);
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [usersFromParent]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoadingBikes(true);
      try {
        const result = await getBikesService();
        if (mounted && result?.success) {
          setInternalBikes(Array.isArray(result.data) ? result.data : []);
        }
      } catch (err) {
        console.error("Error al cargar bicicletas en modal:", err);
      } finally {
        if (mounted) setLoadingBikes(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const usersCache = useMemo(() => {
    if (Array.isArray(usersFromParent) && usersFromParent.length > 0) {
      return usersFromParent;
    }
    return internalUsers;
  }, [usersFromParent, internalUsers]);

  // ========== BÚSQUEDA POR CÉDULA ==========
  // ⚠️ IMPORTANTE: NO borrar userBikes cuando el usuario ya está guardado
  useEffect(() => {
    const cedula = formData.cedula?.trim();

    if (!cedula || cedula.length < 6 || savedUser) {
      // ✅ Solo limpiar si NO está guardado el usuario
      if (!hasRecordId(savedUser?.id)) {
        setFoundUser(null);
        setUserLookupDone(false);
        setSearchingUser(false);
        setUserBikes([]);
      }
      return;
    }

    if (!Array.isArray(usersCache) || usersCache.length === 0) {
      setSearchingUser(false);
      setUserLookupDone(false);
      return;
    }

    setSearchingUser(true);
    setUserLookupDone(false);

    const timeoutId = setTimeout(() => {
      if (!isMounted.current) return;

      const match = usersCache.find((u) => String(u.identification) === String(cedula));

      if (match) {
        setFoundUser(match);

        const bikes = (internalBikes || []).filter((b) => String(b.id_user) === String(match.id));
        setUserBikes(bikes);

        setFormData((prev) => {
          if (prev.nombre || prev.apellido || prev.celular) {
            return prev;
          }
          return {
            ...prev,
            nombre: match.names || prev.nombre,
            apellido: match.lastnames || prev.apellido,
            idType: Number(match.id_type) || prev.idType,
            celular: match.more || match.phone || prev.celular,
          };
        });
      } else {
        setFoundUser(null);
        setUserBikes([]);
      }

      setSearchingUser(false);
      setUserLookupDone(true);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.cedula, usersCache, internalBikes, savedUser]);

  // ========== RECARGAR BICIS CUANDO EL USUARIO SE GUARDA ==========
  // ✅ Esto hace que la tabla siga visible después de guardar
  useEffect(() => {
    if (!savedUser?.id) return;

    const loadUserBikes = async () => {
      try {
        const result = await getBikesService();
        if (result?.success && isMounted.current) {
          const list = Array.isArray(result.data) ? result.data : [];
          setInternalBikes(list);
          const updated = list.filter((b) => String(b.id_user) === String(savedUser.id));
          setUserBikes(updated);
        }
      } catch (err) {
        console.warn("No se pudo cargar las bicis del usuario guardado:", err);
      }
    };

    loadUserBikes();
  }, [savedUser?.id]);

  // ========== SELECCIONAR BICI EXISTENTE ==========
  // ✅ Marca savedBike directamente (funciona si el usuario está guardado)
  const selectExistingBike = useCallback(
    (bike) => {
      if (!bike) return;

      const bikeData = {
        id: bike.id,
        code: bike.code || bike.serie || "",
        color: bike.color || "",
        id_user: bike.id_user || savedUser?.id || foundUser?.id,
        brand: bike.brand || bike.marca || "",
      };

      setFormData((prev) => ({
        ...prev,
        color: bikeData.color || prev.color,
        serie: bikeData.code || prev.serie,
      }));

      setSavedBike(bikeData);
      setBikeError(null);
    },
    [savedUser, foundUser]
  );

  // ========== VALIDACIONES ==========
  const validateField = useCallback((field, value) => {
    let error = "";
    const cleanValue = typeof value === "string" ? value.trim() : value;

    switch (field) {
      case "cedula":
        if (!cleanValue) error = "La cédula es requerida";
        else if (!/^\d+$/.test(cleanValue)) error = "Solo números";
        else if (cleanValue.length < 6 || cleanValue.length > 15) error = "Entre 6 y 15 dígitos";
        break;

      case "idType":
        if (!cleanValue) error = "El tipo de documento es requerido";
        break;

      case "nombre":
        if (!cleanValue) error = "El nombre es requerido";
        else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(cleanValue)) error = "Solo letras y espacios";
        else if (cleanValue.length < 2) error = "Mínimo 2 caracteres";
        else if (cleanValue.length > 50) error = "Máximo 50 caracteres";
        break;

      case "apellido":
        if (!cleanValue) error = "El apellido es requerido";
        else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(cleanValue)) error = "Solo letras y espacios";
        else if (cleanValue.length < 2) error = "Mínimo 2 caracteres";
        else if (cleanValue.length > 50) error = "Máximo 50 caracteres";
        break;

      case "celular":
        if (!cleanValue) error = "El celular es requerido";
        else if (!/^\d+$/.test(cleanValue)) error = "Solo números";
        else if (cleanValue.length < 10 || cleanValue.length > 15) error = "Entre 10 y 15 dígitos";
        break;

      case "color":
        if (!cleanValue) error = "El color es requerido";
        break;

      case "serie":
        if (!cleanValue) error = "El número de serie es requerido";
        else if (!/^[a-zA-Z0-9-]+$/.test(cleanValue)) error = "Solo letras, números y guiones";
        else if (cleanValue.length < 2) error = "Mínimo 2 caracteres";
        else if (cleanValue.length > 30) error = "Máximo 30 caracteres";
        break;

      case "ganchoId":
        if (!cleanValue) error = "Debe seleccionar un gancho";
        break;

      default:
        break;
    }

    return error;
  }, []);

  const handleChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setTouched((prev) => ({ ...prev, [field]: true }));

      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));

      if (field === "cedula" || field === "idType") {
        setSavedUser((prev) => {
          if (!prev) return prev;

          const cedulaChanged = field === "cedula" && String(prev.identification) !== String(value);
          const idTypeChanged = field === "idType" && String(prev.id_type) !== String(value);

          if (cedulaChanged || idTypeChanged) {
            return null;
          }
          return prev;
        });

        if (field === "cedula") {
          setSavedBike(null);
          setFoundUser(null);
          setUserLookupDone(false);
          setUserBikes([]);
        }
      }

      if (field === "color" || field === "serie") {
        setSavedBike(null);
      }
    },
    [validateField]
  );

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      const error = validateField(field, formData[field]);
      setErrors((prev) => ({ ...prev, [field]: error }));
    },
    [validateField, formData]
  );

  const isUserFormValid = useCallback(() => {
    const fields = ["cedula", "idType", "nombre", "apellido", "celular"];
    return fields.every((f) => !validateField(f, formData[f]));
  }, [formData, validateField]);

  const isBikeFormValid = useCallback(() => {
    const fields = ["color", "serie"];
    return fields.every((f) => !validateField(f, formData[f]));
  }, [formData, validateField]);

  const resetForm = useCallback(() => {
    setFormData({
      cedula: "",
      idType: 1,
      nombre: "",
      apellido: "",
      celular: "",
      color: "",
      marca: "",
      serie: "",
      ganchoId: "",
    });
    setTouched({});
    setErrors({});
    setSubmitError(null);
    setUserError(null);
    setBikeError(null);
    setSavedUser(null);
    setSavedBike(null);
    setFoundUser(null);
    setUserLookupDone(false);
    setUserBikes([]);
    setIsSubmitting(false);
  }, []);

  const getNombreCompleto = useCallback(() => {
    return `${formData.nombre} ${formData.apellido}`.trim();
  }, [formData.nombre, formData.apellido]);

  const isFieldError = useCallback(
    (field) => Boolean(touched[field] && errors[field]),
    [touched, errors]
  );

  const getFieldError = useCallback(
    (field) => (isFieldError(field) ? errors[field] : ""),
    [isFieldError, errors]
  );

  const progreso = useCallback(() => {
    let done = 0;
    if (savedUser) done++;
    if (savedBike) done++;
    if (formData.ganchoId) done++;
    return Math.round((done / 3) * 100);
  }, [savedUser, savedBike, formData.ganchoId]);

  const saveUser = useCallback(async () => {
    if (!isUserFormValid()) {
      setUserError("Complete correctamente todos los campos del usuario");
      return { success: false, error: "Formulario de usuario incompleto" };
    }
    if (savingUser) return { success: false, error: "Guardando usuario..." };

    setSavingUser(true);
    setUserError(null);

    try {
      const result = await callbacks.onSaveUser?.(formData);

      if (result?.success) {
        const userData = result.data || result.user;
        if (!hasRecordId(userData?.id)) {
          setUserError(
            "No se recibió el ID del usuario guardado. Búscalo nuevamente por documento antes de continuar."
          );
          return { success: false, error: "Usuario guardado sin identificador" };
        }
        setSavedUser(userData);

        try {
          const usersResult = await getUsersService();
          if (usersResult?.success && isMounted.current) {
            const list = Array.isArray(usersResult.data) ? usersResult.data : [];
            setInternalUsers(list);
          }
          const bikesResult = await getBikesService();
          if (bikesResult?.success && isMounted.current) {
            const list = Array.isArray(bikesResult.data) ? bikesResult.data : [];
            setInternalBikes(list);
            const updated = list.filter((b) => String(b.id_user) === String(userData.id));
            setUserBikes(updated);
          }
        } catch (err) {
          console.warn("No se pudo refrescar usuarios/bicis:", err);
        }

        return { success: true, data: userData };
      }
      setUserError(result?.error || "Error al guardar usuario");
      return { success: false, error: result?.error };
    } catch (error) {
      setUserError(error.message || "Error al guardar usuario");
      return { success: false, error: error.message };
    } finally {
      setSavingUser(false);
    }
  }, [formData, isUserFormValid, savingUser, callbacks]);

  const saveBike = useCallback(async () => {
    if (!hasRecordId(savedUser?.id)) {
      setBikeError("Debe guardar el usuario primero");
      return { success: false, error: "Usuario no guardado" };
    }
    if (!isBikeFormValid()) {
      setBikeError("Complete correctamente los datos de la bicicleta");
      return { success: false, error: "Formulario de bici incompleto" };
    }
    if (savingBike) return { success: false, error: "Guardando bicicleta..." };

    setSavingBike(true);
    setBikeError(null);

    try {
      const bikePayload = {
        code: formData.serie,
        color: formData.color,
        id_user: savedUser.id,
      };

      const result = await callbacks.onSaveBike?.(bikePayload, savedUser);

      if (result?.success) {
        const bikeData = result.data || result.bike;
        if (!hasRecordId(bikeData?.id)) {
          setBikeError(
            "No se recibió el ID de la bicicleta guardada. Selecciónala de la lista antes de continuar."
          );
          return { success: false, error: "Bicicleta guardada sin identificador" };
        }
        setSavedBike({
          ...bikeData,
          brand: formData.marca || bikeData?.brand || "",
        });

        try {
          const bikesResult = await getBikesService();
          if (bikesResult?.success && isMounted.current) {
            const list = Array.isArray(bikesResult.data) ? bikesResult.data : [];
            setInternalBikes(list);
            const updated = list.filter((b) => String(b.id_user) === String(savedUser.id));
            setUserBikes(updated);
          }
        } catch (err) {
          console.warn("No se pudo refrescar la lista de bicis:", err);
        }

        return { success: true, data: bikeData };
      }
      setBikeError(result?.error || "Error al guardar bicicleta");
      return { success: false, error: result?.error };
    } catch (error) {
      setBikeError(error.message || "Error al guardar bicicleta");
      return { success: false, error: error.message };
    } finally {
      setSavingBike(false);
    }
  }, [formData, savedUser, isBikeFormValid, savingBike, callbacks]);

  const submitForm = useCallback(
    async (extraData = {}) => {
      if (!hasRecordId(savedUser?.id)) {
        setSubmitError("Debe guardar el usuario primero");
        return { success: false, error: "Usuario no guardado" };
      }
      if (!hasRecordId(savedBike?.id)) {
        setSubmitError("Debe guardar la bicicleta primero");
        return { success: false, error: "Bicicleta no guardada" };
      }
      if (!formData.ganchoId) {
        setSubmitError("Debe seleccionar un gancho");
        return { success: false, error: "Gancho no seleccionado" };
      }
      if (isSubmitting) return { success: false, error: "Procesando..." };

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const result = await callbacks.onSubmit?.({
          formData,
          user: savedUser,
          bike: savedBike,
          ...extraData,
        });

        if (result?.success) {
          return { success: true, data: result.data };
        }
        setSubmitError(result?.error || "Error al registrar");
        return { success: false, error: result?.error };
      } catch (error) {
        setSubmitError(error.message || "Error al registrar");
        return { success: false, error: error.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, savedUser, savedBike, isSubmitting, callbacks]
  );

  const formState = useMemo(
    () => ({
      formData,
      setFormData,
      touched,
      errors,
      isSubmitting,
      submitError,
      savedUser,
      savedBike,
      savingUser,
      savingBike,
      userError,
      bikeError,
      setSavedUser,
      setSavedBike,
      foundUser,
      searchingUser,
      userLookupDone,
      loadingUsers,
      loadingBikes,
      usersCache,
      userBikes,
      selectExistingBike,
      handleChange,
      handleBlur,
      isFieldError,
      getFieldError,
      getNombreCompleto,
      progreso,
      isUserFormValid,
      isBikeFormValid,
      saveUser,
      saveBike,
      submitForm,
      resetForm,
    }),
    [
      formData,
      touched,
      errors,
      isSubmitting,
      submitError,
      savedUser,
      savedBike,
      savingUser,
      savingBike,
      userError,
      bikeError,
      foundUser,
      searchingUser,
      userLookupDone,
      loadingUsers,
      loadingBikes,
      usersCache,
      userBikes,
      selectExistingBike,
      handleChange,
      handleBlur,
      isFieldError,
      getFieldError,
      getNombreCompleto,
      progreso,
      isUserFormValid,
      isBikeFormValid,
      saveUser,
      saveBike,
      submitForm,
      resetForm,
    ]
  );

  return formState;
};

export default useFormBicicletaModal;
